import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, RotateCcw, Crop as CropIcon } from 'lucide-react';

interface CropInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface ImageCropModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  initialCrop?: { enabled: boolean; top: number; right: number; bottom: number; left: number };
  onApplyCrop: (crop: { enabled: boolean; top: number; right: number; bottom: number; left: number }, croppedUrl?: string) => void;
}

type AspectRatioPreset = 'free' | '1:1' | '16:9' | '9:16' | '4:3';

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  initialCrop,
  onApplyCrop,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Insets as percentage (0 to 100)
  const [crop, setCrop] = useState<CropInsets>({ top: 0, right: 0, bottom: 0, left: 0 });
  const [preset, setPreset] = useState<AspectRatioPreset>('free');
  const [imageSize, setImageSize] = useState<{ width: number; height: number; naturalWidth: number; naturalHeight: number } | null>(null);

  // Drag interaction state
  const dragRef = useRef<{
    handle: string | null;
    startX: number;
    startY: number;
    startCrop: CropInsets;
  }>({
    handle: null,
    startX: 0,
    startY: 0,
    startCrop: { top: 0, right: 0, bottom: 0, left: 0 },
  });

  const [corsMode, setCorsMode] = useState<'anonymous' | undefined>('anonymous');

  // Initialize or reset crop on open
  useEffect(() => {
    if (isOpen) {
      console.log('[ImageCropModal] Modal opened. imageUrl:', imageUrl, 'initialCrop:', initialCrop);
      setCorsMode('anonymous');
      if (initialCrop?.enabled) {
        setCrop({
          top: initialCrop.top || 0,
          right: initialCrop.right || 0,
          bottom: initialCrop.bottom || 0,
          left: initialCrop.left || 0,
        });
      } else {
        setCrop({ top: 0, right: 0, bottom: 0, left: 0 });
      }
      setPreset('free');
    }
  }, [isOpen, initialCrop, imageUrl]);

  const handleImageLoad = () => {
    if (imageRef.current) {
      console.log('[ImageCropModal] Image loaded successfully:', {
        clientWidth: imageRef.current.clientWidth,
        clientHeight: imageRef.current.clientHeight,
        naturalWidth: imageRef.current.naturalWidth,
        naturalHeight: imageRef.current.naturalHeight,
      });
      setImageSize({
        width: imageRef.current.clientWidth,
        height: imageRef.current.clientHeight,
        naturalWidth: imageRef.current.naturalWidth,
        naturalHeight: imageRef.current.naturalHeight,
      });
    }
  };

  // Immediate check if image is already cached
  useEffect(() => {
    if (isOpen && imageRef.current && imageRef.current.complete && imageRef.current.naturalWidth > 0) {
      handleImageLoad();
    }
  }, [isOpen, imageUrl]);

  const handleImageError = () => {
    if (corsMode === 'anonymous') {
      console.warn('[ImageCropModal] Anonymous CORS load failed. Retrying without crossOrigin for visual crop...');
      setCorsMode(undefined);
    } else {
      console.error('[ImageCropModal] Failed to load image at URL:', imageUrl);
    }
  };

  const handleReset = () => {
    setCrop({ top: 0, right: 0, bottom: 0, left: 0 });
    setPreset('free');
  };

  const handlePresetSelect = (selected: AspectRatioPreset) => {
    setPreset(selected);
    if (!imageSize) return;

    if (selected === 'free') return;

    let targetRatio = 1;
    if (selected === '1:1') targetRatio = 1;
    if (selected === '16:9') targetRatio = 16 / 9;
    if (selected === '9:16') targetRatio = 9 / 16;
    if (selected === '4:3') targetRatio = 4 / 3;

    const imgWidth = imageSize.width;
    const imgHeight = imageSize.height;
    const currentRatio = imgWidth / imgHeight;

    let cropWidth = imgWidth;
    let cropHeight = imgHeight;

    if (currentRatio > targetRatio) {
      cropWidth = imgHeight * targetRatio;
      const horizontalDiffPct = ((imgWidth - cropWidth) / imgWidth / 2) * 100;
      setCrop({
        top: 0,
        bottom: 0,
        left: Math.round(horizontalDiffPct * 10) / 10,
        right: Math.round(horizontalDiffPct * 10) / 10,
      });
    } else {
      cropHeight = imgWidth / targetRatio;
      const verticalDiffPct = ((imgHeight - cropHeight) / imgHeight / 2) * 100;
      setCrop({
        top: Math.round(verticalDiffPct * 10) / 10,
        bottom: Math.round(verticalDiffPct * 10) / 10,
        left: 0,
        right: 0,
      });
    }
  };

  const startDrag = (handle: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      handle,
      startX: e.clientX,
      startY: e.clientY,
      startCrop: { ...crop },
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!dragRef.current.handle || !imageSize) return;
      const dx = moveEvent.clientX - dragRef.current.startX;
      const dy = moveEvent.clientY - dragRef.current.startY;

      const dxPct = (dx / imageSize.width) * 100;
      const dyPct = (dy / imageSize.height) * 100;

      const currentHandle = dragRef.current.handle;
      const prev = dragRef.current.startCrop;

      setCrop(() => {
        let newTop = prev.top;
        let newBottom = prev.bottom;
        let newLeft = prev.left;
        let newRight = prev.right;

        if (currentHandle === 'move') {
          const widthPct = 100 - prev.left - prev.right;
          const heightPct = 100 - prev.top - prev.bottom;

          newLeft = Math.max(0, Math.min(100 - widthPct, prev.left + dxPct));
          newRight = Math.max(0, 100 - newLeft - widthPct);

          newTop = Math.max(0, Math.min(100 - heightPct, prev.top + dyPct));
          newBottom = Math.max(0, 100 - newTop - heightPct);
        } else {
          if (currentHandle.includes('top')) {
            newTop = Math.max(0, Math.min(100 - prev.bottom - 10, prev.top + dyPct));
          }
          if (currentHandle.includes('bottom')) {
            newBottom = Math.max(0, Math.min(100 - prev.top - 10, prev.bottom - dyPct));
          }
          if (currentHandle.includes('left')) {
            newLeft = Math.max(0, Math.min(100 - prev.right - 10, prev.left + dxPct));
          }
          if (currentHandle.includes('right')) {
            newRight = Math.max(0, Math.min(100 - prev.left - 10, prev.right - dxPct));
          }
        }

        return {
          top: Math.round(newTop * 10) / 10,
          right: Math.round(newRight * 10) / 10,
          bottom: Math.round(newBottom * 10) / 10,
          left: Math.round(newLeft * 10) / 10,
        };
      });
    };

    const handleMouseUp = () => {
      console.log('[ImageCropModal] Drag ended. Final crop:', crop);
      dragRef.current.handle = null;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleApply = useCallback(() => {
    const isCropped = crop.top > 0 || crop.right > 0 || crop.bottom > 0 || crop.left > 0;
    console.log('[ImageCropModal] handleApply invoked:', {
      crop,
      isCropped,
      hasImageRef: !!imageRef.current,
      naturalWidth: imageRef.current?.naturalWidth,
      naturalHeight: imageRef.current?.naturalHeight,
    });

    if (imageRef.current && isCropped && imageRef.current.naturalWidth > 0 && imageRef.current.naturalHeight > 0) {
      try {
        const img = imageRef.current;
        const nw = img.naturalWidth;
        const nh = img.naturalHeight;

        const srcX = (crop.left / 100) * nw;
        const srcY = (crop.top / 100) * nh;
        const srcW = nw - srcX - (crop.right / 100) * nw;
        const srcH = nh - srcY - (crop.bottom / 100) * nh;

        console.log('[ImageCropModal] Creating canvas with slice:', { srcX, srcY, srcW, srcH });

        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(srcW));
        canvas.height = Math.max(1, Math.round(srcH));
        const ctx = canvas.getContext('2d');

        if (ctx) {
          ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, canvas.width, canvas.height);
          const croppedDataUrl = canvas.toDataURL('image/png');
          console.log('[ImageCropModal] Canvas extraction successful! Passing croppedUrl (length: ' + croppedDataUrl.length + ') to onApplyCrop.');
          onApplyCrop({ enabled: true, ...crop }, croppedDataUrl);
          onClose();
          return;
        }
      } catch (err) {
        console.warn('[ImageCropModal] Canvas extraction failed (likely CORS tainted canvas), falling back to CSS crop:', err);
      }
    }

    console.log('[ImageCropModal] Calling onApplyCrop without croppedUrl (CSS crop fallback or uncropped).');
    onApplyCrop({ enabled: isCropped, ...crop });
    onClose();
  }, [crop, onApplyCrop, onClose]);

  // Keyboard shortcut listener (Enter = Apply, Escape = Cancel)
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleApply();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, handleApply]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="flex flex-col bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden border border-gray-100 max-h-[90vh] scale-100 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-green-50 text-[#30A84B] rounded-lg">
              <CropIcon className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Crop Image</h2>
              <p className="text-xs text-gray-500">Drag edges, corner handles, or slide the box to frame your crop</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Aspect Ratio Preset Bar */}
        <div className="flex items-center justify-between px-6 py-3 bg-gray-50/80 border-b border-gray-100">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-semibold text-gray-500 mr-1">Aspect Ratio:</span>
            {(['free', '1:1', '16:9', '9:16', '4:3'] as AspectRatioPreset[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => handlePresetSelect(r)}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${
                  preset === r
                    ? 'bg-[#30A84B] text-white shadow-xs'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200/60'
                }`}
              >
                {r === 'free' ? 'Freeform' : r}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Reset</span>
          </button>
        </div>

        {/* Crop Workspace */}
        <div
          ref={containerRef}
          className="relative flex items-center justify-center bg-gray-950 p-6 flex-1 min-h-[380px] overflow-hidden select-none"
        >
          <div className="relative inline-block max-w-full max-h-[500px]">
            {/* The Image */}
            <img
              ref={imageRef}
              src={imageUrl}
              crossOrigin={corsMode}
              alt="Crop target"
              onLoad={handleImageLoad}
              onError={handleImageError}
              className="max-h-[500px] max-w-full object-contain pointer-events-none rounded-xs"
            />

            {/* Dark Mask Outside Crop Area */}
            <div
              className="absolute inset-0 bg-black/60 pointer-events-none"
              style={{
                clipPath: `polygon(
                  0% 0%, 100% 0%, 100% 100%, 0% 100%,
                  0% 0%,
                  ${crop.left}% ${crop.top}%,
                  ${crop.left}% ${100 - crop.bottom}%,
                  ${100 - crop.right}% ${100 - crop.bottom}%,
                  ${100 - crop.right}% ${crop.top}%,
                  ${crop.left}% ${crop.top}%
                )`,
              }}
            />

            {/* Interactive Crop Boundary Box */}
            <div
              onMouseDown={(e) => startDrag('move', e)}
              className="absolute border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.5)] cursor-move pointer-events-auto"
              style={{
                top: `${crop.top}%`,
                left: `${crop.left}%`,
                right: `${crop.right}%`,
                bottom: `${crop.bottom}%`,
              }}
            >
              {/* Rule of Thirds Grid */}
              <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 pointer-events-none opacity-40">
                <div className="border-r border-b border-white" />
                <div className="border-r border-b border-white" />
                <div className="border-b border-white" />
                <div className="border-r border-b border-white" />
                <div className="border-r border-b border-white" />
                <div className="border-b border-white" />
                <div className="border-r border-white" />
                <div className="border-r border-white" />
                <div />
              </div>

              {/* 4 Corner Handles */}
              <div
                onMouseDown={(e) => startDrag('top-left', e)}
                className="absolute -top-2.5 -left-2.5 w-5 h-5 bg-white border-2 border-[#30A84B] rounded-full cursor-nwse-resize shadow-lg hover:scale-110 transition-transform"
              />
              <div
                onMouseDown={(e) => startDrag('top-right', e)}
                className="absolute -top-2.5 -right-2.5 w-5 h-5 bg-white border-2 border-[#30A84B] rounded-full cursor-nesw-resize shadow-lg hover:scale-110 transition-transform"
              />
              <div
                onMouseDown={(e) => startDrag('bottom-left', e)}
                className="absolute -bottom-2.5 -left-2.5 w-5 h-5 bg-white border-2 border-[#30A84B] rounded-full cursor-nesw-resize shadow-lg hover:scale-110 transition-transform"
              />
              <div
                onMouseDown={(e) => startDrag('bottom-right', e)}
                className="absolute -bottom-2.5 -right-2.5 w-5 h-5 bg-white border-2 border-[#30A84B] rounded-full cursor-nwse-resize shadow-lg hover:scale-110 transition-transform"
              />

              {/* 4 Edge Handles */}
              <div
                onMouseDown={(e) => startDrag('top', e)}
                className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-10 h-3 bg-white border border-[#30A84B] rounded-full cursor-ns-resize shadow-md hover:scale-105 transition-transform"
              />
              <div
                onMouseDown={(e) => startDrag('bottom', e)}
                className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-10 h-3 bg-white border border-[#30A84B] rounded-full cursor-ns-resize shadow-md hover:scale-105 transition-transform"
              />
              <div
                onMouseDown={(e) => startDrag('left', e)}
                className="absolute top-1/2 -left-1.5 -translate-y-1/2 w-3 h-10 bg-white border border-[#30A84B] rounded-full cursor-ew-resize shadow-md hover:scale-105 transition-transform"
              />
              <div
                onMouseDown={(e) => startDrag('right', e)}
                className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3 h-10 bg-white border border-[#30A84B] rounded-full cursor-ew-resize shadow-md hover:scale-105 transition-transform"
              />
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-6 py-4 bg-white border-t border-gray-100">
          <p className="text-xs text-gray-500">
            {crop.top > 0 || crop.right > 0 || crop.bottom > 0 || crop.left > 0
              ? `Cropped: Top ${crop.top}% | Right ${crop.right}% | Bottom ${crop.bottom}% | Left ${crop.left}% (Press Enter to Apply)`
              : 'Full image (drag handles or choose a preset)'}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-[#30A84B] hover:bg-[#288e3f] shadow-sm transition-all"
            >
              <Check className="w-4 h-4" />
              <span>Apply Crop</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
